import { Controller } from "@hotwired/stimulus"
import { trackEvent } from "../utils/analytics"
import { showStatusBadge } from "../utils/statusBadge"
import { PUSH_SUBSCRIPTION_OWNER_KEY } from "../pwa/pushSubscription"

export default class extends Controller {
  static targets = ["toggle", "message"]

  connect() {
    this.checkPermissionState()
  }

  async checkPermissionState() {
    if (!("Notification" in window)) {
      this.showMessage("Push notifications are not supported in this browser", "error")
      this.toggleTarget.disabled = true
      return
    }

    const permission = Notification.permission

    if (permission === "denied") {
      this.showMessage("Notifications are blocked. Please enable them in your browser settings", "error")
      this.toggleTarget.checked = false
    } else if (permission === "granted") {
      if (localStorage.getItem("pushNotificationsOptedOut") === "true") {
        this.toggleTarget.checked = false
        this.showMessage("Notifications are disabled", "disabled")
        return
      }
      this.toggleTarget.checked = true
      this.showMessage("Notifications are enabled", "enabled")
      const hasSubscription = await this.hasActivePushSubscription()

      if (!hasSubscription && localStorage.getItem("pendingPushSubscription") === "true") {
        await this.subscribeToPush()
        localStorage.removeItem("pendingPushSubscription")
        this.toggleTarget.checked = true
        this.showMessage("Notifications are enabled", "enabled")
        return
      }

      this.toggleTarget.checked = hasSubscription
      if (hasSubscription) {
        this.showMessage("Notifications are enabled", "enabled")
      } else {
        this.showMessage("Notifications are disabled", "disabled")
      }
    } else {
      this.toggleTarget.checked = false
      this.showMessage("Notifications are disabled", "disabled")
    }
  }

  async hasActivePushSubscription() {
    try {
      const registration = await navigator.serviceWorker.getRegistration()
      if (!registration) return false
      const subscription = await registration.pushManager.getSubscription()
      return !!subscription
    } catch {
      return false
    }
  }

  async toggle(event) {
    if (!("Notification" in window)) {
      event.preventDefault()
      this.showMessage("Push notifications are not supported in this browser", "error")
      return
    }

    if (event.target.checked) {
      try {
        trackEvent("push_permission_prompt_shown", { source: "settings" })
        const permission = await Notification.requestPermission()

        if (permission === "granted") {
          trackEvent("push_permission_granted", { source: "settings" })
          localStorage.removeItem("pushNotificationsOptedOut")
          await this.subscribeToPush()
          this.showMessage("Notifications are enabled", "enabled")
        } else if (permission === "denied") {
          trackEvent("push_permission_denied", { source: "settings" })
          this.toggleTarget.checked = false
          this.showMessage("Notifications are blocked. Please enable them in your browser settings", "error")
        } else {
          trackEvent("push_permission_dismissed", { source: "settings" })
          this.toggleTarget.checked = false
          this.showMessage("Notification permission dismissed", "disabled")
        }
      } catch (error) {
        trackEvent("push_subscription_error", { source: "settings", error: String(error).slice(0, 255) })
        console.error("Error requesting notification permission:", error)
        this.toggleTarget.checked = false
        this.showMessage("Error requesting notification permission", "error")
      }
    } else {
      localStorage.setItem("pushNotificationsOptedOut", "true")
      await this.unsubscribeFromPush()
      this.showMessage("Notifications are disabled", "disabled")
    }
  }

  async subscribeToPush() {
    await navigator.serviceWorker.register("/service-worker.js")
    const registration = await navigator.serviceWorker.ready
    const vapidResponse = await fetch("/push_subscription")
    const { vapid_public_key } = await vapidResponse.json()

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: this.urlBase64ToUint8Array(vapid_public_key)
    })

    const key = subscription.getKey("p256dh")
    const auth = subscription.getKey("auth")

    sessionStorage.setItem("pushEndpoint", subscription.endpoint)

    const response = await fetch("/push_subscription", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({
        push_subscription: {
          endpoint: subscription.endpoint,
          p256dh_key: btoa(String.fromCharCode(...new Uint8Array(key))),
          auth_key: btoa(String.fromCharCode(...new Uint8Array(auth)))
        }
      })
    })

    const userId = document.body.dataset.userId
    if (response.ok && userId) localStorage.setItem(PUSH_SUBSCRIPTION_OWNER_KEY, userId)
  }

  async unsubscribeFromPush() {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await subscription.unsubscribe()
        sessionStorage.removeItem("pushEndpoint")
        localStorage.removeItem(PUSH_SUBSCRIPTION_OWNER_KEY)

        await fetch("/push_subscription", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
          },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        })
      }
    } catch (error) {
      trackEvent("push_subscription_error", { source: "settings", action: "unsubscribe", error: String(error).slice(0, 255) })
      console.error("Failed to unsubscribe from push:", error)
    }
  }

  showMessage(text, tone = "disabled") {
    if (!this.hasMessageTarget) return
    showStatusBadge(this.messageTarget, text, tone)
  }

  async sendTest() {
    if (!("Notification" in window)) {
      this.showMessage("Push notifications are not supported in this browser", "error")
      return
    }
    if (Notification.permission !== "granted") {
      this.showMessage("Allow notifications using the toggle above, then try again", "error")
      return
    }
    if (!this.toggleTarget.checked) {
      this.showMessage("Turn on the toggle above, then try again", "error")
      return
    }

    const response = await fetch("/push_subscription/send_test", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      }
    })
    const data = await response.json()
    if (data.sent > 0) {
      this.showMessage("Test notification sent", "enabled")
    } else {
      this.showMessage("No subscriptions found. Try toggling notifications off and on.", "error")
    }
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
